import { queryOptions, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut } from "../client";
import type {
  ActorSummary,
  ClaimIdentityPayload,
  DeviceSession,
  HandleAvailability,
  PreferencesPayload,
  Session,
} from "../types";
import { qk } from "./qk";
import { bumpViewerEpoch } from "./viewer-epoch";

// Identity lives on the real auth server (server/). MSW has no handlers for these paths, so its
// worker passes them through to the network.

export const sessionQuery = () =>
  queryOptions({
    queryKey: qk.session(),
    queryFn: () => apiGet<Session>("/api/session"),
    staleTime: 60_000,
    retry: 1,
  });

export const handleAvailabilityQuery = (handle: string) =>
  queryOptions({
    queryKey: qk.handles.availability(handle),
    queryFn: () => apiGet<HandleAvailability>(`/api/handles/${encodeURIComponent(handle)}`),
    staleTime: 15_000,
    enabled: handle.length > 0,
  });

export const deviceSessionsQuery = () =>
  queryOptions({
    queryKey: qk.account.sessions(),
    queryFn: () => apiGet<DeviceSession[]>("/api/auth/list-sessions"),
  });

const isDetailKey = (key: readonly unknown[]) => key[1] === "detail";

/**
 * Refreshes catalog queries for a new viewer. Every product and store response carries viewer-relative fields
 * (`inWishlist`, `isFollowing`), so all of them must be re-read, but not all the same way:
 *
 * - Detail keys (a product or store page, and the lists nested under it) are reset, so a mounted page refetches
 *   with the new viewer instead of showing the previous viewer's state, and a toggle can never read it back.
 * - Everything else (the catalog grid, store lists, studio rows) is invalidated, not reset: a mounted infinite
 *   grid keeps its loaded pages and scroll position while every page refetches in the background, instead of
 *   collapsing to a skeleton and re-reading only page 1.
 */
export function refreshViewerQueries(queryClient: QueryClient) {
  // Mutations started under the previous viewer must not roll their snapshots back over this viewer's data.
  bumpViewerEpoch(queryClient);
  for (const root of [qk.products.all(), qk.stores.all()]) {
    void queryClient.resetQueries({ queryKey: root, predicate: (query) => isDetailKey(query.queryKey) });
    void queryClient.invalidateQueries({ queryKey: root, predicate: (query) => !isDetailKey(query.queryKey) });
  }
}

type WithItems<T> = { items: T[] };
type InfinitePages<T> = { pages: WithItems<T>[]; pageParams: unknown[] };

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object";
}

/** Maps every item of a cached list, whatever its shape: an array, a page of `items`, or infinite pages. */
function mapListItems(value: unknown, mapItem: (item: unknown) => unknown): unknown {
  if (Array.isArray(value)) return value.map(mapItem);
  if (!isObject(value)) return value;
  if (Array.isArray((value as InfinitePages<unknown>).pages)) {
    const infinite = value as InfinitePages<unknown>;
    return { ...infinite, pages: infinite.pages.map((page) => mapListItems(page, mapItem)) };
  }
  if (Array.isArray((value as WithItems<unknown>).items)) {
    return { ...value, items: (value as WithItems<unknown>).items.map(mapItem) };
  }
  return value;
}

function anonymousProduct(item: unknown): unknown {
  if (!isObject(item)) return item;
  let next = item;
  if (item.inWishlist === true) next = { ...next, inWishlist: false };
  if (isObject(item.store) && item.store.isFollowing === true) next = { ...next, store: { ...item.store, isFollowing: false } };
  return next;
}

function anonymousStore(item: unknown): unknown {
  // Follower counts stay as they are: the signed-out viewer's follow was already counted, and the refetch settles it.
  return isObject(item) && item.isFollowing === true ? { ...item, isFollowing: false } : item;
}

/**
 * The anonymous viewer's state is known without asking the server: nothing is wishlisted or followed. Writing it into
 * every cached list before the refetch means the signed-out viewer never sees the previous viewer's hearts or
 * "Following" buttons, even if the refetch is slow or fails, while loaded pages and scroll position stay put.
 */
export async function clearViewerStateFromLists(queryClient: QueryClient) {
  bumpViewerEpoch(queryClient);
  const lists = { predicate: (query: { queryKey: readonly unknown[] }) => !isDetailKey(query.queryKey) };
  // Awaited, not fired: a list fetch started for the signed-in viewer must not land after the clear, and a
  // cancelled fetch reverts its query's data asynchronously, which would otherwise overwrite the clear.
  await Promise.all([
    queryClient.cancelQueries({ queryKey: qk.products.all(), ...lists }),
    queryClient.cancelQueries({ queryKey: qk.stores.all(), ...lists }),
  ]);
  queryClient.setQueriesData({ queryKey: qk.products.all(), ...lists }, (previous: unknown) =>
    previous === undefined ? previous : mapListItems(previous, anonymousProduct),
  );
  queryClient.setQueriesData({ queryKey: qk.stores.all(), ...lists }, (previous: unknown) =>
    previous === undefined ? previous : mapListItems(previous, anonymousStore),
  );
}

/** Always re-reads the session from the server — used right after it changes. */
function refreshSession(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.fetchQuery({ ...sessionQuery(), staleTime: 0 });
}

export function useSendSignInCode() {
  return useMutation({
    mutationFn: (email: string) =>
      apiPost<{ success: boolean }>("/api/auth/email-otp/send-verification-otp", { email, type: "sign-in" }),
  });
}

export function useVerifySignInCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ email, code }: { email: string; code: string }) => {
      await apiPost("/api/auth/sign-in/email-otp", { email, otp: code });
      queryClient.removeQueries({ queryKey: qk.account.all() });
      // Refreshed, not removed: a mounted product or store observer refetches with the new viewer instead of
      // being orphaned.
      refreshViewerQueries(queryClient);
      return refreshSession(queryClient);
    },
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost("/api/auth/sign-out", {}),
    onSuccess: async () => {
      queryClient.setQueryData<Session>(qk.session(), { status: "anonymous" });
      queryClient.removeQueries({ queryKey: qk.account.all() });
      // Product and store responses carry the signed-out viewer's inWishlist/isFollowing. Refreshed, not
      // removed, so a mounted observer refetches as the anonymous viewer instead of being orphaned. Lists get the
      // known anonymous values first, so they never show the signed-out viewer's state while they refetch.
      await clearViewerStateFromLists(queryClient);
      refreshViewerQueries(queryClient);
    },
  });
}

export function useClaimIdentity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClaimIdentityPayload) =>
      apiPost<{ person: ActorSummary }>("/api/identity/claim", payload),
    onSettled: () => refreshSession(queryClient),
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PreferencesPayload) => apiPut<PreferencesPayload>("/api/me/preferences", payload),
    onSuccess: () => refreshSession(queryClient),
  });
}

export function useRevokeDeviceSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => apiPost("/api/auth/revoke-session", { token }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.account.sessions() }),
  });
}

export function useRevokeOtherDeviceSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost("/api/auth/revoke-other-sessions", {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.account.sessions() }),
  });
}
